import { useEffect, useRef, useCallback, useId, useState } from 'react';
import {
  Box, Flex, Heading, Text, Button, IconButton, Select, Icon, Image,
  useColorModeValue, HStack,
} from '@chakra-ui/react';
import { LuCheck, LuArrowLeft, LuArrowRight, LuMapPin } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../../i18n/languages';
import { COUNTRIES } from '../../utils/onboardingCountries';
import useIsPhone from '../../hooks/useIsPhone';
import { useAuth } from '../../contexts/AuthContext';
import useKeyboardInset from '../../hooks/useKeyboardInset';
import { readableInk } from '../../utils/colorContrast';
import ConfirmationDialog from '../shared/ConfirmationDialog';

// The page both onboarding variants are drawn on: the rail, the phone's
// progress line, the card, its footer, the phone's action bar and the press
// debounce. The owner flow (OnboardingPage) and the invited member's
// (GuestOnboarding) differ only in their list of steps, their header and the
// body of each stop. See docs/specs/2026-09-23-full-page-onboarding-design.md,
// "Invited members: an onboarding of their own".

export function StageRail({ steps, stepIndex }) {
  const { t } = useTranslation('onboarding');
  return (
    <Box
      w="220px"
      flexShrink={0}
      pl="22px"
      position="relative"
      display={{ base: 'none', md: 'block' }}
      _before={{
        content: '""',
        position: 'absolute',
        left: '4px',
        top: '6px',
        bottom: '6px',
        width: '1px',
        bg: 'redesign.border',
      }}
    >
      {steps.map((key, i) => {
        const isDone = i < stepIndex;
        const isActive = i === stepIndex;
        return (
          <Box
            key={key}
            position="relative"
            py="10px"
            fontSize="15px"
            fontWeight={isActive ? 700 : isDone ? 600 : 500}
            color={
              isActive ? 'redesign.accent.blue'
                : isDone ? 'redesign.heading'
                  : 'redesign.text.muted'
            }
            _before={isActive ? {
              content: '""',
              position: 'absolute',
              left: '-22px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '9px',
              height: '9px',
              borderRadius: '50%',
              bg: 'redesign.accent.blue',
            } : undefined}
          >
            {t(`stages.${key}`)}
          </Box>
        );
      })}
    </Box>
  );
}

// A span, not a <label>: the language field is a row of buttons. So a field
// names itself with the label's `id` (aria-labelledby) instead.
export function FieldLabel({ children, hint, id }) {
  // The hint is read, so it is muted rather than faint: faint is about 1.4:1
  // on the page.
  const isPhone = useIsPhone();
  return (
    <Text as="span" id={id} display="block" fontSize="13px" fontWeight={600} color="redesign.text" mb="8px">
      {children}
      {hint && (
        <Text as="span" color={isPhone ? 'phone.muted' : 'redesign.text.muted'} fontWeight={400}> {hint}</Text>
      )}
    </Text>
  );
}

// The card's own measure, from the mockup. The page's width is derived from it,
// so it is named once rather than repeated.
export const CARD_W = '680px';

// The card's buttons. Every one is 14px/600 on a 10px radius — the mockup's
// single button scale, so a footer never mixes two.
export const BTN = {
  fontSize: '14px', fontWeight: 600, borderRadius: '10px', h: 'auto',
  px: '18px', py: '10px',
};

// `accent` paints the button with the colour of the thing it creates — the
// account step's Continue turns green for a savings account, as the mockup
// draws it. Without one it is the standing blue.
export function PrimaryButton({ accent, ...props }) {
  const bg = accent || 'redesign.accent.fill';
  return (
    <Button
      {...BTN}
      bg={bg}
      // White on a light account colour (the default savings green) is
      // 2.3:1; those get the dark ink.
      color={accent ? readableInk(accent) : 'white'}
      _hover={{ bg, filter: 'brightness(0.94)' }}
      _active={{ bg, filter: 'brightness(0.88)' }}
      _disabled={{ opacity: 0.5, cursor: 'default', bg }}
      rightIcon={<Icon as={LuArrowRight} boxSize="16px" />}
      {...props}
    />
  );
}

// --- Phone ---------------------------------------------------------------
// On the phone the rail becomes a progress line, the card's chrome goes (its
// border and gutters would take 40px a side from a 390px screen), and the
// footer becomes a bar fixed to the bottom, in thumb reach, as the app's other
// phone screens draw their Save bar.

// The action bar: 8px + a 52px button + 8px, above the home indicator.
export const PHONE_BAR_H = '68px';

// A phone held sideways is 800px wide: fields that long read as a banner. The
// column keeps a portrait phone's measure, centred, and the bar's buttons too.
export const PHONE_COL_W = '600px';

// The rail, as one segment per step: done and current filled, the rest
// waiting. `aside` sits at the other end of the "Step x of y" line — the guest
// flow puts whose space this is there once its banner has scrolled away.
export function StageProgress({ steps, stepIndex, aside }) {
  const { t } = useTranslation('onboarding');
  return (
    <Box>
      <Flex gap="4px" role="presentation">
        {steps.map((key, i) => (
          <Box
            key={key}
            flex="1"
            h="4px"
            borderRadius="full"
            bg={i <= stepIndex ? 'redesign.accent.blue' : 'redesign.border'}
            transition="background 0.2s"
          />
        ))}
      </Flex>
      <Flex align="center" justify="space-between" gap="12px" mt="10px">
        <Text textStyle="phoneCaption" color="phone.muted" flexShrink={0}>
          {t('wizard.stepOf', { current: stepIndex + 1, total: steps.length })}
        </Text>
        {aside}
      </Flex>
    </Box>
  );
}

// The phone's footer. It rides the keyboard while one is up (iOS shrinks
// neither the layout viewport nor dvh for it), so Continue is reachable right
// after typing the account's name.
export function PhoneActionBar({ children }) {
  const { inset, keyboardUp } = useKeyboardInset();
  return (
    <Box
      position="fixed"
      left={0}
      right={0}
      bottom={keyboardUp ? `${inset}px` : 0}
      zIndex="sticky"
      bg="redesign.bg.surface"
      borderTop="1px solid"
      borderColor="redesign.border"
      pt="8px"
      pb={keyboardUp ? '8px' : 'calc(env(safe-area-inset-bottom) + 8px)'}
      pl="calc(16px + env(safe-area-inset-left))"
      pr="calc(16px + env(safe-area-inset-right))"
    >
      <Flex gap="10px" align="center" maxW={PHONE_COL_W} mx="auto">{children}</Flex>
    </Box>
  );
}

// The footer's presses are taken one at a time. A double tap used to act
// twice: during the welcome step's await it seeded two cash accounts and
// skipped the account step, and right after a step change the phone's bar
// has the next step's button under the same finger. So a press is ignored
// while a step change is under way, just after one, or just after another
// press. `advancing` is the "under way" half: a handler that awaits sets it,
// and clears it if the step does not change after all.
export function usePressGate(step) {
  const advancing = useRef(false);
  const stepChangedAt = useRef(0);
  const lastPress = useRef(0);
  useEffect(() => {
    advancing.current = false;
    stepChangedAt.current = performance.now();
  }, [step]);
  const acceptPress = useCallback(() => {
    const now = performance.now();
    if (advancing.current || now - stepChangedAt.current < 350 || now - lastPress.current < 400) return false;
    lastPress.current = now;
    return true;
  }, []);
  return { acceptPress, advancing };
}

// The phone's fields: the full width, and 52px, like its other forms.
function useFieldWidth() {
  return useIsPhone() ? 'none' : '340px';
}

export function LanguageField({ onSelect }) {
  const { t, i18n } = useTranslation('onboarding');
  const isPhone = useIsPhone();
  const fieldW = useFieldWidth();
  const currentLng = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const labelId = useId();
  return (
    <Box mb="20px">
      <FieldLabel id={labelId}>{t('wizard.welcome.language')}</FieldLabel>
      {/* A named group of toggles: the chosen one is said, not only coloured
          and ticked. */}
      <HStack spacing="10px" maxW={fieldW} role="group" aria-labelledby={labelId}>
        {SUPPORTED_LANGUAGES.map((l) => {
          const selected = currentLng === l.code;
          return (
            <Button
              key={l.code}
              flex="1"
              h={isPhone ? '52px' : 'auto'}
              px="14px"
              py="11px"
              fontSize={isPhone ? '16px' : '14px'}
              fontWeight={600}
              borderRadius="12px"
              borderWidth="1px"
              // White on `accent.fill` is 5.2:1 in both modes.
              borderColor={selected ? 'redesign.accent.fill' : 'redesign.border.strong'}
              bg={selected ? 'redesign.accent.fill' : 'redesign.bg.surface'}
              color={selected ? 'white' : 'redesign.text'}
              _hover={{ bg: selected ? 'redesign.accent.fill' : 'redesign.bg.hover' }}
              leftIcon={selected ? <Icon as={LuCheck} boxSize="14px" strokeWidth={2.2} /> : undefined}
              aria-pressed={selected}
              onClick={() => onSelect(l.code)}
            >
              {l.nativeLabel}
            </Button>
          );
        })}
      </HStack>
    </Box>
  );
}

export function CountryField({ value, onChange, hint }) {
  const { t } = useTranslation('onboarding');
  const isPhone = useIsPhone();
  const fieldW = useFieldWidth();
  const labelId = useId();
  return (
    <Box mb="20px">
      <FieldLabel hint={hint} id={labelId}>{t('wizard.welcome.country')}</FieldLabel>
      {/* The pin belongs inside the box, left of the text. `pl` on a Chakra
          Select lands on its wrapper, which insets the field instead of
          padding it — so the room for the icon is made on the field itself. */}
      <Box position="relative" maxW={fieldW} sx={{ '& select': { paddingInlineStart: '39px' } }}>
        <Icon
          as={LuMapPin}
          boxSize="18px"
          strokeWidth={1.7}
          color="redesign.text"
          position="absolute"
          left="13px"
          top="50%"
          transform="translateY(-50%)"
          pointerEvents="none"
          zIndex={1}
        />
        <Select
          // Named by the label above; otherwise it is read only as its value.
          aria-labelledby={labelId}
          placeholder={t('wizard.country.placeholder')}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          h={isPhone ? '52px' : '42px'}
          fontSize="14px"
          color="redesign.text"
          borderRadius="12px"
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>{c.name} · {c.currency}</option>
          ))}
        </Select>
      </Box>
    </Box>
  );
}

/**
 * The page itself: brand, header, rail, the step's card and its footer.
 *
 * - `steps` / `step`: the variant's list of stops and the one on screen. The
 *   rail, the phone's progress line and the card's title follow them.
 * - `header`: what stands above the rail on the computer. Without one, the
 *   owner flow's "Set up your account".
 * - `phoneTop`: what stands above the card on the phone. Without one, the
 *   progress line.
 * - `desc`: the step's standing description (never on the last step, which
 *   leads with its own badge and headline).
 * - `primary` ({ label, onClick, isDisabled, isLoading, accent }), `onBack`
 *   and an optional `onSkip`: the footer. The last step has none; it carries
 *   its own.
 */
/**
 * Who is signed in, and the way out to another account.
 *
 * Onboarding holds the user: every app route sends them back here until it is
 * finished. Without this, someone who signed in with the wrong Google account
 * (a new, empty one) was trapped in that account's onboarding, with no way to
 * reach the account they meant. Signing out lands on /login, whose Google
 * button always shows the account chooser (`prompt=select_account`). It sits
 * by the brand, quiet, so it is not mistaken for the way forward.
 *
 * An invited member has already joined the host's space with this account, so
 * `hostName` asks first: the invitation is used, and another account signing
 * in after them gets no access to that space.
 */
function AccountSwitch({ hostName }) {
  const { t } = useTranslation('onboarding');
  const isPhone = useIsPhone();
  const { user, logout } = useAuth();
  const [confirming, setConfirming] = useState(false);
  return (
    <>
      <Flex align="center" gap="10px" ml="auto" minW={0}>
        {!isPhone && user?.email && (
          <Text fontSize="13px" color="redesign.text.muted" noOfLines={1} m={0}>
            {user.email}
          </Text>
        )}
        <Button
          variant="link"
          fontSize="13px"
          fontWeight={600}
          color={isPhone ? 'phone.link' : 'redesign.text'}
          minH={isPhone ? '44px' : undefined}
          flexShrink={0}
          onClick={() => (hostName ? setConfirming(true) : logout())}
          aria-label={user?.email ? t('switchAccount.aria', { email: user.email }) : undefined}
        >
          {t('switchAccount.label')}
        </Button>
      </Flex>
      {hostName && (
        <ConfirmationDialog
          isOpen={confirming}
          onClose={() => setConfirming(false)}
          onConfirm={() => logout()}
          title={t('switchAccount.confirmTitle')}
          message={t('switchAccount.confirmGuest', { name: hostName, email: user?.email || '' })}
          confirmText={t('switchAccount.confirm')}
          confirmColorScheme="blue"
        />
      )}
    </>
  );
}

export function OnboardingFrame({
  steps, step, header, phoneTop, cardMaxW = CARD_W, desc, primary, onBack, onSkip, hostName, children,
}) {
  const { t } = useTranslation('onboarding');
  const isPhone = useIsPhone();
  const brandSrc = useColorModeValue('/brand/app-icon-light.svg', '/brand/app-icon-dark.svg');

  const key = steps[step];
  const isLast = step === steps.length - 1;

  // Toasts on the phone sit above the tab bar, which this page doesn't have.
  // Put them above its own action bar instead — the last step has none.
  useEffect(() => {
    if (!isPhone) return undefined;
    const rootStyle = document.documentElement.style;
    rootStyle.setProperty('--mk-toast-above', isLast ? '0px' : PHONE_BAR_H);
    return () => rootStyle.removeProperty('--mk-toast-above');
  }, [isPhone, isLast]);

  // One element tree for both layouts, with the phone and the computer
  // differing only in props and in which optional pieces render. The step's
  // body sits at the same place in both, so a window resized across the
  // breakpoint (split view, a snapped window, zoom) keeps the half-filled
  // account form and a running import instead of remounting them. A phone
  // turned sideways is still the phone, so rotating changes nothing.
  const outerProps = isPhone
    ? {
      minH: '100dvh',
      pt: 'calc(env(safe-area-inset-top) + 20px)',
      pl: 'calc(16px + env(safe-area-inset-left))',
      pr: 'calc(16px + env(safe-area-inset-right))',
      pb: isLast
        ? 'calc(env(safe-area-inset-bottom) + 24px)'
        : `calc(${PHONE_BAR_H} + env(safe-area-inset-bottom) + 24px)`,
    }
    : { minH: '100vh', px: { base: 5, md: 10 }, py: { base: 8, md: '56px' } };
  // On the phone the card's chrome goes: its border and gutters would take
  // 40px a side from a 390px screen.
  const cardProps = isPhone
    ? { flex: '1', minW: 0 }
    : {
      flex: '1',
      maxW: cardMaxW,
      bg: 'redesign.bg.surface',
      borderWidth: '1px',
      borderColor: 'redesign.border',
      borderRadius: '20px',
    };

  return (
    <Box bg="redesign.bg.page" {...outerProps}>
      {/* Rail + gap + card is exactly what this page is wide; anything past it
          is margin. The mockup's artboard was 1180px, where that nearly filled
          the frame and read as balanced — on a real 1400px window the same
          fixed layout just sits against the left edge. So the block is capped
          at its own width and centred, and the brand and title line up with
          the rail rather than with the window. */}
      <Box maxW={isPhone ? PHONE_COL_W : `calc(220px + 64px + ${CARD_W})`} mx="auto">
        <Flex align="center" gap={isPhone ? '8px' : '10px'} mb={isPhone ? '20px' : { base: 6, md: '40px' }}>
          <Image
            src={brandSrc}
            alt=""
            boxSize={isPhone ? '24px' : '28px'}
            borderRadius={isPhone ? '7px' : '8px'}
          />
          <Text fontSize="15px" fontWeight={700} letterSpacing="-0.01em" color="redesign.heading">
            Mi Kapital
          </Text>
          <AccountSwitch hostName={hostName} />
        </Flex>

        {isPhone ? (
          phoneTop || <StageProgress steps={steps} stepIndex={step} />
        ) : (
          header || (
            <Heading
              as="h1"
              fontSize={{ base: '22px', md: '27px' }}
              fontWeight={700}
              letterSpacing="-0.02em"
              color="redesign.heading"
              m={0}
              mb={{ base: 6, md: '36px' }}
            >
              {t('pageTitle')}
            </Heading>
          )
        )}

        <Flex gap={isPhone ? 0 : { base: 6, md: '64px' }} align="flex-start">
          {!isPhone && <StageRail steps={steps} stepIndex={step} />}

          <Box {...cardProps}>
            {isPhone ? (
              <Heading
                as="h1"
                fontSize="26px"
                lineHeight="32px"
                fontWeight={700}
                letterSpacing="-0.02em"
                color="redesign.heading"
                mt="6px"
                mb={isLast ? '20px' : '8px'}
              >
                {t(`stages.${key}`)}
              </Heading>
            ) : (
              <Box px={{ base: 5, md: '32px' }} py="24px" borderBottomWidth="1px" borderBottomColor="redesign.border">
                <Text fontSize="18px" fontWeight={700} letterSpacing="-0.01em" color="redesign.heading" m={0}>
                  {t(`stages.${key}`)}
                </Text>
              </Box>
            )}

            <Box {...(isPhone ? {} : { px: { base: 5, md: '32px' }, pt: '28px', pb: '30px' })}>
              {/* The last step leads with its own badge and headline, so the
                  standing description would land above them, out of order. */}
              {!isLast && desc && (
                isPhone ? (
                  <Text textStyle="phoneBody" color="redesign.text.muted" mb="24px">
                    {desc}
                  </Text>
                ) : (
                  <Text fontSize="14px" lineHeight="1.6" color="redesign.text.muted" m={0} mb="26px" maxW="480px">
                    {desc}
                  </Text>
                )
              )}

              {children}

              {/* No ✕ and no way out: onboarding is the only thing this user
                  can do until it is finished. The last step carries its own
                  footer — it has no "next". The phone's footer is the bar
                  below. */}
              {!isLast && !isPhone && (
                <Flex
                  align="center"
                  justify={step === 0 ? 'flex-end' : 'space-between'}
                  mt="22px"
                  pt="20px"
                  borderTopWidth="1px"
                  borderTopColor="redesign.border"
                >
                  {step > 0 && (
                    <Button
                      {...BTN}
                      variant="outline"
                      borderColor="redesign.border.strong"
                      color="redesign.text"
                      bg="redesign.bg.surface"
                      _hover={{ bg: 'redesign.bg.hover' }}
                      leftIcon={<Icon as={LuArrowLeft} boxSize="16px" />}
                      onClick={onBack}
                    >
                      {t('wizard.back')}
                    </Button>
                  )}
                  <HStack spacing="8px">
                    {onSkip && (
                      <Button {...BTN} variant="ghost" color="redesign.text.muted" onClick={onSkip}>
                        {t('wizard.skip')}
                      </Button>
                    )}
                    <PrimaryButton
                      onClick={primary.onClick}
                      isDisabled={primary.isDisabled}
                      isLoading={primary.isLoading}
                      accent={primary.accent}
                    >
                      {primary.label}
                    </PrimaryButton>
                  </HStack>
                </Flex>
              )}
            </Box>
          </Box>
        </Flex>
      </Box>

      {/* The phone's footer: the last step carries its own actions. */}
      {isPhone && !isLast && (
        <PhoneActionBar>
          {step > 0 && (
            <IconButton
              aria-label={t('wizard.back')}
              icon={<Icon as={LuArrowLeft} boxSize="22px" />}
              variant="outline"
              w="52px"
              h="52px"
              minW="52px"
              borderRadius="14px"
              borderColor="redesign.border.strong"
              color="redesign.heading"
              bg="redesign.bg.surface"
              onClick={onBack}
            />
          )}
          {onSkip && (
            <Button
              variant="ghost"
              h="52px"
              px="14px"
              fontSize="17px"
              fontWeight={600}
              color="phone.muted"
              onClick={onSkip}
            >
              {t('wizard.skip')}
            </Button>
          )}
          {/* Always the phone's own blue, not the account's colour: white on
              a light account colour (green, yellow) is under 2.5:1, and a
              cash account's near-black disappears into the dark-mode bar. */}
          <Button
            variant="phonePrimary"
            flex="1"
            minW={0}
            rightIcon={<Icon as={LuArrowRight} boxSize="18px" />}
            onClick={primary.onClick}
            isDisabled={primary.isDisabled}
            isLoading={primary.isLoading}
          >
            {primary.label}
          </Button>
        </PhoneActionBar>
      )}
    </Box>
  );
}
